import { useQueryClient } from '@tanstack/react-query';
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  ReactNode,
} from 'react';
import { Platform } from 'react-native';

import { useAuth } from './AuthContext';
import { useEndImpersonation } from '../hooks/mutations/useEndImpersonation';
import { useStartImpersonation } from '../hooks/mutations/useStartImpersonation';
import {
  useImpersonationSessionQuery,
  impersonationKeys,
} from '../hooks/queries/useImpersonationSessionQuery';
import { setImpersonationRestoreHandler } from '../lib/queryClient';
import { getAuthService } from '../services/auth/authServiceFactory';
import { bindDomainMembershipIfNeeded } from '../services/bindDomainMembership';
import { impersonationService } from '../services/impersonationService';
import {
  ImpersonationMode,
  ImpersonationSession,
  ImpersonationUser,
} from '../types/Impersonation';
import { showToast } from '../utils/toast';

const GRACEFUL_END_BUFFER_SECONDS = 30;

export interface ImpersonationContextType {
  isImpersonating: boolean;
  session: ImpersonationSession | null;
  targetUser: ImpersonationUser | null;
  mode: ImpersonationMode | null;
  timeRemaining: number | null;
  startImpersonation: (
    userId: number,
    mode?: ImpersonationMode,
  ) => Promise<void>;
  endImpersonation: () => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

const ImpersonationContext = createContext<
  ImpersonationContextType | undefined
>(undefined);

interface ImpersonationProviderProps {
  children: ReactNode;
}

export const ImpersonationProvider: React.FC<ImpersonationProviderProps> = ({
  children,
}) => {
  const { isAuthenticated, refreshAuth } = useAuth();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const wasImpersonatingRef = useRef(false);
  const endingManuallyRef = useRef(false);
  const restoringRef = useRef(false);
  const gracefulEndTriggeredRef = useRef(false);
  const sessionOwnerUserIdRef = useRef<number | undefined>(undefined);

  const sessionQuery = useImpersonationSessionQuery(isAuthenticated);
  const startMutation = useStartImpersonation();
  const endMutation = useEndImpersonation();

  const sessionData = sessionQuery.data;
  const isImpersonating = sessionData?.is_impersonating ?? false;

  const session: ImpersonationSession | null = useMemo(() => {
    if (!sessionData?.is_impersonating || !sessionData.session_id) {
      return null;
    }
    return {
      session_id: sessionData.session_id,
      mode: sessionData.mode!,
      target_user: sessionData.target_user!,
      impersonator: sessionData.impersonator!,
      expires_at: sessionData.expires_at!,
      time_remaining_seconds: sessionData.time_remaining_seconds,
    };
  }, [sessionData]);

  const targetUser = session?.target_user ?? null;
  const mode = session?.mode ?? null;

  useEffect(() => {
    const impersonatorId = session?.impersonator?.user_id;
    if (
      isImpersonating &&
      typeof impersonatorId === 'number' &&
      Number.isInteger(impersonatorId) &&
      impersonatorId > 0
    ) {
      sessionOwnerUserIdRef.current = impersonatorId;
    }
  }, [isImpersonating, session?.impersonator?.user_id]);

  const flushBindForRestoredOwner = useCallback(() => {
    const sessionOwnerUserId = sessionOwnerUserIdRef.current;
    if (sessionOwnerUserId === undefined) {
      return;
    }
    void Promise.resolve(
      bindDomainMembershipIfNeeded({ sessionOwnerUserId }),
    ).catch(() => undefined);
  }, []);

  const restoreNativeThenMaybeBind = useCallback(async (): Promise<void> => {
    if (Platform.OS === 'web') {
      flushBindForRestoredOwner();
      return;
    }
    const authService = getAuthService();
    let restored = false;
    if (authService.restoreStashedTokens) {
      try {
        restored = (await authService.restoreStashedTokens()) === true;
      } catch (restoreErr: unknown) {
        console.error(
          'Failed to restore stashed tokens (non-blocking):',
          restoreErr,
        );
      }
    }
    if (restored) {
      flushBindForRestoredOwner();
    }
  }, [flushBindForRestoredOwner]);

  // Register 401 restore handler when impersonating (Path A — web and native)
  // This allows the QueryClient's 401 handler to attempt identity restoration
  // before triggering session expiration/logout.
  useEffect(() => {
    if (isImpersonating && Platform.OS === 'web') {
      const handler = async (): Promise<boolean> => {
        try {
          restoringRef.current = true;
          await impersonationService.restoreIdentity();
          flushBindForRestoredOwner();
          await refreshAuth();
          showToast.info(
            'Your impersonation session has expired. You have been returned to your own session.',
          );
          // Prevent Path B useEffect from firing when queries refetch
          // and isImpersonating becomes false after successful restore
          wasImpersonatingRef.current = false;
          return true;
        } catch (err: unknown) {
          console.error('Failed to restore identity via 401 handler:', err);
          showToast.info(
            'Your impersonation session has expired. Please log in again.',
          );
          return false;
        } finally {
          restoringRef.current = false;
        }
      };
      setImpersonationRestoreHandler(handler);
    } else if (isImpersonating && Platform.OS !== 'web') {
      // Native Path A: restore stashed tokens on 401 (same race condition as Story 2.3)
      const handler = async (): Promise<boolean> => {
        try {
          restoringRef.current = true;
          const authService = getAuthService();
          const restored = authService.restoreStashedTokens
            ? await authService.restoreStashedTokens()
            : false;
          if (restored) {
            flushBindForRestoredOwner();
            await refreshAuth();
            showToast.info(
              'Your impersonation session has expired. You have been returned to your own session.',
            );
            wasImpersonatingRef.current = false;
            return true;
          }
          showToast.info(
            'Your impersonation session has expired. Please log in again.',
          );
          return false;
        } catch (err: unknown) {
          console.error(
            'Failed to restore stashed tokens via 401 handler:',
            err,
          );
          showToast.info(
            'Your impersonation session has expired. Please log in again.',
          );
          return false;
        } finally {
          restoringRef.current = false;
        }
      };
      setImpersonationRestoreHandler(handler);
    } else {
      setImpersonationRestoreHandler(null);
    }

    return () => {
      setImpersonationRestoreHandler(null);
    };
  }, [isImpersonating, refreshAuth, flushBindForRestoredOwner]);

  // Client-side countdown timer
  useEffect(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    if (!session?.expires_at) {
      setTimeRemaining(null);
      return;
    }

    const calculateRemaining = () => {
      const expiresAt = new Date(session.expires_at).getTime();
      const now = Date.now();
      const remaining = Math.max(0, Math.floor((expiresAt - now) / 1000));
      setTimeRemaining(remaining);
    };

    calculateRemaining();
    timerRef.current = setInterval(calculateRemaining, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [session?.expires_at]);

  // Graceful end: trigger end-session ~30s before JWT expiry (Story 4.1)
  useEffect(() => {
    if (
      timeRemaining !== null &&
      timeRemaining > 0 &&
      timeRemaining <= GRACEFUL_END_BUFFER_SECONDS &&
      !gracefulEndTriggeredRef.current &&
      !endingManuallyRef.current &&
      !restoringRef.current &&
      session?.session_id
    ) {
      gracefulEndTriggeredRef.current = true;
      endingManuallyRef.current = true; // Suppress Path B during graceful end

      endMutation
        .mutateAsync(session.session_id)
        .then(async () => {
          await restoreNativeThenMaybeBind();
          queryClient.clear();
          return refreshAuth();
        })
        .then(() => {
          showToast.info(
            'Your impersonation session has ended. You have been returned to your own session.',
          );
        })
        .catch((err: unknown) => {
          // 404 or 410 — session already ended/expired/not found, not a real failure
          const isAlreadyGone =
            (err !== null &&
              typeof err === 'object' &&
              'status' in err &&
              ((err as { status: number }).status === 410 ||
                (err as { status: number }).status === 404)) ||
            (err instanceof Error &&
              (err.message.includes('IMPERSONATION_SESSION_EXPIRED') ||
                err.message.includes('IMPERSONATION_SESSION_NOT_FOUND')));

          if (isAlreadyGone) {
            void restoreNativeThenMaybeBind().then(() => {
              queryClient.clear();
              refreshAuth().catch(() => {});
            });
          } else {
            // Real failure — let restoration mechanisms handle it
            console.error(
              'Graceful end-session failed, restoration will handle:',
              err,
            );
            gracefulEndTriggeredRef.current = false;
            endingManuallyRef.current = false;
          }
        });
    }
  }, [
    timeRemaining,
    session?.session_id,
    endMutation,
    queryClient,
    refreshAuth,
    restoreNativeThenMaybeBind,
  ]);

  // Session expiry detection (Path B): detect transition from impersonating → not impersonating
  // when it wasn't triggered by a manual endImpersonation call.
  // IMPORTANT: Only treat it as session expiry when the session query has
  // successfully returned data confirming no active session. If the query is in
  // an error/loading state (e.g., from a transient 403 on a mutation clearing
  // the cache), the transition is spurious and should be ignored.
  useEffect(() => {
    const sessionQueryHasData =
      sessionQuery.isSuccess && sessionQuery.data !== undefined;
    if (
      wasImpersonatingRef.current &&
      !isImpersonating &&
      !endingManuallyRef.current &&
      sessionQueryHasData
    ) {
      // Path B: Session expired server-side with valid JWT (polling detected it)
      // Skip cache clear if the 401 handler (Path A) is already handling restore
      if (!restoringRef.current) {
        queryClient.clear();
      }
      if (Platform.OS === 'web' && !restoringRef.current) {
        restoringRef.current = true;
        impersonationService
          .restoreIdentity()
          .then(() => {
            flushBindForRestoredOwner();
            // Restore succeeded — pick up the newly-promoted auth_token cookie.
            // Handle refreshAuth failure separately: identity was restored server-side
            // even if the client-side refresh hits a transient error.
            return refreshAuth().catch((refreshErr: unknown) => {
              console.error(
                'Failed to refresh auth after successful identity restore:',
                refreshErr,
              );
            });
          })
          .then(() => {
            showToast.info(
              'Your impersonation session has expired. You have been returned to your own session.',
            );
          })
          .catch((err: unknown) => {
            console.error(
              'Failed to restore identity after impersonation expiry:',
              err,
            );
            // Restore cookie also expired — user must re-login via Okta
            showToast.info(
              'Your impersonation session has expired. Please log in again.',
            );
            // Still call refreshAuth to settle auth state (will be unauthenticated → Okta redirect)
            return refreshAuth().catch(() => {});
          })
          .finally(() => {
            restoringRef.current = false;
          });
      } else if (Platform.OS !== 'web' && !restoringRef.current) {
        // Native Path B: restore stashed tokens when polling detects session expiry
        restoringRef.current = true;
        const authService = getAuthService();
        const restorePromise = authService.restoreStashedTokens
          ? authService.restoreStashedTokens()
          : Promise.resolve(false);

        restorePromise
          .then(restored => {
            if (restored) {
              flushBindForRestoredOwner();
              return refreshAuth()
                .then(() => {
                  showToast.info(
                    'Your impersonation session has expired. You have been returned to your own session.',
                  );
                })
                .catch((refreshErr: unknown) => {
                  console.error(
                    'Failed to refresh auth after native token restore:',
                    refreshErr,
                  );
                  showToast.info(
                    'Your impersonation session has expired. You have been returned to your own session.',
                  );
                });
            } else {
              showToast.info(
                'Your impersonation session has expired. Please log in again.',
              );
              return refreshAuth().catch(() => {});
            }
          })
          .catch((err: unknown) => {
            console.error(
              'Failed to restore stashed tokens after impersonation expiry:',
              err,
            );
            showToast.info(
              'Your impersonation session has expired. Please log in again.',
            );
            return refreshAuth().catch(() => {});
          })
          .finally(() => {
            restoringRef.current = false;
          });
      }
    }
    // Reset graceful end guard when transitioning TO impersonating (new session)
    if (isImpersonating && !wasImpersonatingRef.current) {
      gracefulEndTriggeredRef.current = false;
    }
    // Only update the ref when we have authoritative data, not on transient states
    if (sessionQueryHasData || isImpersonating) {
      wasImpersonatingRef.current = isImpersonating;
    }
    endingManuallyRef.current = false;
  }, [
    isImpersonating,
    queryClient,
    refreshAuth,
    sessionQuery.isSuccess,
    sessionQuery.data,
    flushBindForRestoredOwner,
  ]);

  const handleStartImpersonation = useCallback(
    async (userId: number, impersonationMode?: ImpersonationMode) => {
      setError(null);
      try {
        // Stash impersonator's tokens before impersonation starts (native only)
        if (Platform.OS !== 'web') {
          const authService = getAuthService();
          if (authService.stashTokensForImpersonation) {
            await authService.stashTokensForImpersonation();
          }
        }
        const response = await startMutation.mutateAsync({
          userId,
          mode: impersonationMode,
        });
        // Native: store the impersonation JWT so subsequent API calls use it
        if (Platform.OS !== 'web' && response.access_token) {
          const authService = getAuthService();
          if (authService.setToken) {
            try {
              await authService.setToken(response.access_token);
            } catch (tokenErr: unknown) {
              // Storage failed — restore original tokens to avoid limbo state
              if (authService.restoreStashedTokens) {
                await authService.restoreStashedTokens().catch(() => {});
              }
              throw tokenErr;
            }
          }
        }
        // Backend swaps JWT cookie (web); clear auth cache and re-fetch user identity
        queryClient.clear();
        await refreshAuth();
        // Explicit refetch — queryClient.clear() races with refreshAuth's enabled flag
        await queryClient.refetchQueries({
          queryKey: impersonationKeys.session(),
        });
      } catch (err) {
        const message =
          err instanceof Error ? err.message : 'Failed to start impersonation';
        setError(message);
        throw err;
      }
    },
    [startMutation, queryClient, refreshAuth],
  );

  const handleEndImpersonation = useCallback(async () => {
    if (!session?.session_id) return;
    setError(null);
    // Flag suppresses the expiry-detection toast during intentional end.
    // The expiry useEffect resets this to false once isImpersonating transitions.
    endingManuallyRef.current = true;
    try {
      await endMutation.mutateAsync(session.session_id);
      await restoreNativeThenMaybeBind();
      // Backend restores JWT cookie (web); clear auth cache and re-fetch user identity
      queryClient.clear();
      await refreshAuth();
      // Explicit refetch — queryClient.clear() races with refreshAuth's enabled flag
      await queryClient.refetchQueries({
        queryKey: impersonationKeys.session(),
      });
    } catch (err: unknown) {
      // 404 or 410 — session already ended/expired/not found, treat as successful end
      const isAlreadyGone =
        (err !== null &&
          typeof err === 'object' &&
          'status' in err &&
          ((err as { status: number }).status === 410 ||
            (err as { status: number }).status === 404)) ||
        (err instanceof Error &&
          (err.message.includes('IMPERSONATION_SESSION_EXPIRED') ||
            err.message.includes('IMPERSONATION_SESSION_NOT_FOUND')));
      if (isAlreadyGone) {
        await restoreNativeThenMaybeBind();
        queryClient.clear();
        await refreshAuth().catch((restoreErr: unknown) => {
          console.error(
            'Failed to restore session after end-session:',
            restoreErr,
          );
        });
        // Explicit refetch — queryClient.clear() races with refreshAuth's enabled flag
        await queryClient.refetchQueries({
          queryKey: impersonationKeys.session(),
        });
        return;
      }
      // Real error — reset flag so expiry detection remains functional
      const message =
        err instanceof Error ? err.message : 'Failed to end impersonation';
      setError(message);
      endingManuallyRef.current = false;
      throw err;
    }
  }, [
    endMutation,
    session?.session_id,
    queryClient,
    refreshAuth,
    restoreNativeThenMaybeBind,
  ]);

  const isLoading =
    sessionQuery.isLoading || startMutation.isPending || endMutation.isPending;

  const contextValue: ImpersonationContextType = useMemo(
    () => ({
      isImpersonating,
      session,
      targetUser,
      mode,
      timeRemaining,
      startImpersonation: handleStartImpersonation,
      endImpersonation: handleEndImpersonation,
      isLoading,
      error,
    }),
    [
      isImpersonating,
      session,
      targetUser,
      mode,
      timeRemaining,
      handleStartImpersonation,
      handleEndImpersonation,
      isLoading,
      error,
    ],
  );

  return (
    <ImpersonationContext.Provider value={contextValue}>
      {children}
    </ImpersonationContext.Provider>
  );
};

export const useImpersonation = (): ImpersonationContextType => {
  const context = useContext(ImpersonationContext);
  if (context === undefined) {
    throw new Error(
      'useImpersonation must be used within an ImpersonationProvider',
    );
  }
  return context;
};
